import api from './api';
import type { StockCount, StockCountLine, PaginatedResponse, StockCountStatus } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/stock-count`;

export interface CreateCountPayload {
  locationId?: string | null;
  countedAt?: string | null;
  countedBy?: string;
  notes?: string;
}

export interface UpdateCountPayload {
  countedAt?: string | null;
  countedBy?: string;
  notes?: string;
}

/** IMPORT-ROUNDTRIP.1 — result of starting a count from refused import rows. */
export interface PrefillCountResult {
  countId: string;
  /** false when the location's open draft was joined instead. */
  createdCount: boolean;
  added: number;
  updated: number;
  skipped: Array<{ inventoryItemId: string; reason: string }>;
}

/** INVENTORY-TRUST.1b — a line whose expected quantity moved between entry and posting. */
export interface ExpectedShift {
  lineId: string;
  inventoryItemId: string;
  previousExpected: number;
  refreshedExpected: number;
}

export interface PostCountResult {
  count: StockCount;
  movementsPosted: number;
  /** Lines whose expectedQty was refreshed from live stock at posting. */
  expectedShifts: ExpectedShift[];
}

export interface BulkLoadResult {
  added: number;
  lines: StockCountLine[];
}

// ── Inventory Cycle Report (STOCKCOUNT-RECON.1) ────────────────────────────────

export interface InventoryCycleReportRow {
  inventoryItemId: string;
  shortId: string | null;
  itemName: string | null;
  itemType: string | null;
  category: string;
  subcategory: string;
  sizeValue: number | null;
  sizeUnit: string | null;
  locationName: string | null;
  openingQuantity: number;
  received: number;
  transferIn: number;
  adjustmentPlus: number;
  salesDepletion: number;
  waste: number;
  transferOut: number;
  adjustmentMinus: number;
  reversalVoid: number;
  expectedQuantity: number;
  physicalQuantity: number;
  varianceQuantity: number;
  unitCost: number | null;
  expectedValue: number | null;
  physicalValue: number | null;
  varianceValue: number | null;
}

export interface InventoryCycleReportMovement {
  date: string;
  inventoryItemId: string;
  shortId: string | null;
  itemName: string | null;
  location: string | null;
  movementType: string;
  quantity: number;
  unitCost: number | null;
  totalCost: number | null;
  bucket: string | null;
  sourceDocumentType: string | null;
  sourceDocumentId: string | null;
  /** POS-ORDER-NUMBER.1 — human-readable; null on most rows. See MovementSourceDocument. */
  sourceDocumentNumber: string | null;
  postedBy: string | null;
  notes: string | null;
}

export interface InventoryCycleReportMeta {
  reportName: string;
  venueId: string;
  venueName: string | null;
  locationId: string | null;
  locationName: string | null;
  currentCount: { id: string; postedAt: string | null; countedBy: string | null };
  previousCount: { id: string; postedAt: string | null } | null;
  periodStart: string | null;
  periodEnd: string | null;
  openingBasis: 'PREVIOUS_COUNT' | 'ASSUMED_ZERO_INCEPTION';
  costBasis: string;
  generatedAt: string;
  notes: string[];
}

export interface InventoryCycleReport {
  meta: InventoryCycleReportMeta;
  rows: InventoryCycleReportRow[];
  movements: InventoryCycleReportMovement[];
}

const stockCountService = {
  getCounts: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    status?: StockCountStatus,
  ): Promise<PaginatedResponse<StockCount>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (status) params.status = status;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  getCount: async (
    venueId: string,
    countId: string,
  ): Promise<{ count: StockCount; lines: StockCountLine[] }> => {
    const response = await api.get(`${base(venueId)}/${countId}`);
    return response.data;
  },

  /**
   * IMPORT-ROUNDTRIP.1 — a draft count for one location with these quantities
   * entered as counted (import rows refused as opening stock). Joins the
   * location's open draft if it has one. Posts nothing.
   */
  prefillCount: async (
    venueId: string,
    data: { locationId: string; countedAt?: string; lines: Array<{ inventoryItemId: string; countedQty: number }> },
  ): Promise<PrefillCountResult> => {
    const response = await api.post(`${base(venueId)}/prefill`, data);
    return response.data;
  },

  /** Create an empty draft count. Lines are not auto-populated. */
  createCount: async (
    venueId: string,
    data: CreateCountPayload,
  ): Promise<StockCount> => {
    const response = await api.post(base(venueId), data);
    return response.data;
  },

  /** Update header fields (countedAt, countedBy, notes). Draft only. */
  updateCount: async (
    venueId: string,
    countId: string,
    data: UpdateCountPayload,
  ): Promise<StockCount> => {
    const response = await api.patch(`${base(venueId)}/${countId}`, data);
    return response.data;
  },

  /**
   * Add a single line by inventoryItemId.
   * Freezes expectedQty from InventoryBalance at this moment.
   * Draft only. Line scope must match count scope.
   */
  addLine: async (
    venueId: string,
    countId: string,
    inventoryItemId: string,
  ): Promise<StockCountLine> => {
    const response = await api.post(`${base(venueId)}/${countId}/lines`, { inventoryItemId });
    return response.data;
  },

  /**
   * Bulk-add all InventoryItems in count scope not already on the count.
   * Each line's expectedQty is frozen at the time of this call.
   * Draft only.
   * @param categoryId — restrict to a specific category; 'none' = unassigned; omit for all.
   */
  bulkLoadLines: async (
    venueId: string,
    countId: string,
    categoryId?: string | 'none',
  ): Promise<BulkLoadResult> => {
    const body = categoryId ? { categoryId } : undefined;
    const response = await api.post(`${base(venueId)}/${countId}/lines/bulk-load`, body);
    return response.data;
  },

  /**
   * Update a single line. Draft only.
   *
   * COUNT-LINE-NOTES.1 — each field is sent ONLY when the caller passes it.
   * The backend leaves an omitted field alone, so a quantity edit cannot wipe a
   * note and a note edit cannot wipe the quantity. Pass countedQty: null to
   * clear the count deliberately.
   */
  updateLine: async (
    venueId: string,
    countId: string,
    lineId: string,
    changes: { countedQty?: number | null; notes?: string | null },
  ): Promise<StockCountLine> => {
    const response = await api.patch(`${base(venueId)}/${countId}/lines/${lineId}`, changes);
    return response.data;
  },

  /** Remove a line from the count. Draft only. */
  removeLine: async (
    venueId: string,
    countId: string,
    lineId: string,
  ): Promise<void> => {
    await api.delete(`${base(venueId)}/${countId}/lines/${lineId}`);
  },

  /**
   * Post the count.
   * Creates COUNT_VARIANCE InventoryMovements for all lines where varianceQty !== 0.
   * Zero-variance and null-countedQty lines are skipped.
   * Updates InventoryBalance via $inc. Atomic (MongoDB transaction).
   * INVENTORY-TRUST.1b: expected quantities are refreshed from live balances
   * inside the posting transaction — variance reflects stock at post time, and
   * any refreshed lines are reported back via expectedShifts.
   * One-way action — count becomes immutable after this.
   */
  postCount: async (
    venueId: string,
    countId: string,
  ): Promise<PostCountResult> => {
    const response = await api.post(`${base(venueId)}/${countId}/post`);
    return response.data;
  },

  /** Delete a draft count. Not allowed after posting. */
  deleteCount: async (venueId: string, countId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${countId}`);
  },

  /**
   * STOCKCOUNT-RECON.1 — Inventory Cycle Report for a POSTED count (read-only).
   * Returns report JSON; the frontend renders it into a multi-sheet .xlsx.
   */
  getInventoryCycleReport: async (
    venueId: string,
    countId: string,
  ): Promise<InventoryCycleReport> => {
    const response = await api.get(`${base(venueId)}/${countId}/inventory-cycle-report`);
    return response.data as InventoryCycleReport;
  },
};

export default stockCountService;
